import { AlertTriangle, RefreshCw } from 'lucide-react';
import clsx from 'clsx';
import type { AdAccount, TokenStatus } from '@/shared/types/entities';
import { AppBadge } from '@/shared/components/ui/AppBadge/AppBadge';
import { AppButton } from '@/shared/components/ui/AppButton/AppButton';
import { formatTimeAgo, formatTimeRemaining } from '@/shared/utils/format-relative-time';
import styles from './AccountCard.module.scss';

export interface AccountCardProps {
  account: AdAccount;
  onDisconnect?: (id: string) => void;
  onSync?: (id: string) => void;
  onReconnect?: (id: string) => void;
}

const TOKEN_STATUS_BADGE_VARIANT: Partial<Record<TokenStatus, 'success' | 'warning' | 'error'>> = {
  valid: 'success',
  expiring: 'warning',
  expired: 'error',
};

const TOKEN_STATUS_LABELS: Partial<Record<TokenStatus, string>> = {
  valid: 'Token valid',
  expiring: 'Token expiring soon',
  expired: 'Token expired',
};

function formatGoogleCustomerId(id: string): string {
  const digits = id.replace(/\D/g, '');
  if (digits.length !== 10) return id;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function AccountCard({ account, onDisconnect, onSync, onReconnect }: AccountCardProps) {
  const showWarningBanner = account.tokenStatus === 'expiring' || account.tokenStatus === 'expired';

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.name}>{account.accountName}</span>
        </div>
        <span className={styles.platform}>{account.platform}</span>
      </div>

      <div className={styles.healthRow}>
        <AppBadge variant={TOKEN_STATUS_BADGE_VARIANT[account.tokenStatus] ?? 'neutral'} size="sm">
          {TOKEN_STATUS_LABELS[account.tokenStatus] ?? 'Unknown status'}
        </AppBadge>

        {account.loginCustomerId && (
          <AppBadge variant="neutral" size="sm">
            via Manager {formatGoogleCustomerId(account.loginCustomerId)}
          </AppBadge>
        )}

        {account.tokenExpiresAt && account.tokenStatus !== 'expired' && (
          <span className={styles.expiryInfo}>{formatTimeRemaining(account.tokenExpiresAt)}</span>
        )}

        <span className={styles.syncInfo}>Last synced: {formatTimeAgo(account.lastSyncedAt)}</span>
      </div>

      {showWarningBanner && (
        <div
          className={clsx(
            styles.warningBanner,
            account.tokenStatus === 'expired'
              ? styles.warningBannerExpired
              : styles.warningBannerExpiring,
          )}
        >
          <span className={styles.warningText}>
            <AlertTriangle size={14} />
            {account.tokenStatus === 'expired'
              ? 'Token has expired. Reconnect to continue using this account.'
              : 'Token is expiring soon. Reconnect to avoid disruption.'}
          </span>
          {onReconnect && (
            <AppButton variant="secondary" size="sm" onClick={() => onReconnect(account.id)}>
              <RefreshCw size={14} />
              Reconnect
            </AppButton>
          )}
        </div>
      )}

      <div className={styles.actions}>
        {onSync && (
          <AppButton type="button" variant="ghost" size="sm" onClick={() => onSync(account.id)}>
            Sync
          </AppButton>
        )}
        {onDisconnect && (
          <AppButton
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => onDisconnect(account.id)}
          >
            Disconnect
          </AppButton>
        )}
      </div>
    </div>
  );
}
